import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useFetchSfQuery } from './salesforce-hooks';
import {
  DEFAULT_TIME_PERIOD,
  FiscalPeriodRow,
  selectTimeFilter,
  TimePeriodId,
  TimeSelection,
} from '../support/fiscalTimePeriods';

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
// The fiscal calendar is an auxiliary lookup; the page's data must never wait on it indefinitely.
const CALENDAR_TIMEOUT_MS = 8000;

const toRecords = (data: unknown): any[] => (Array.isArray(data) ? data : []);

// Only the fiscal month / quarter / year that contain today — all the dropdown ever needs.
const currentPeriodsSoql = (today: Date) => {
  const day = format(today, 'yyyy-MM-dd');
  return (
    `SELECT Type, StartDate, EndDate FROM Period WHERE Type IN ('Month', 'Quarter', 'Year') ` +
    `AND StartDate <= ${day} AND EndDate >= ${day} ORDER BY StartDate`
  );
};

/**
 * State behind a Leader tab's time dropdown (Current Fiscal Month / Quarter / Year / All). Reads the
 * current fiscal periods from Salesforce `Period` records (one shared, cached lookup across tabs) and resolves
 * the choice into a `current_time_filter` value and a date window. Each caller keeps its own selection and
 * picks its own default (the Salesforce tab opens on the current quarter, Campaigns on All).
 */
export function useFiscalTimePeriod(defaultPeriod: TimePeriodId = DEFAULT_TIME_PERIOD): {
  periodId: TimePeriodId;
  setPeriodId: (id: TimePeriodId) => void;
  periods: FiscalPeriodRow[];
} & TimeSelection {
  const [periodId, setPeriodId] = useState<TimePeriodId>(defaultPeriod);
  // One "today" per mount keeps the SOQL (and its cache key) stable across renders.
  const today = useMemo(() => new Date(), []);

  const query = useFetchSfQuery(currentPeriodsSoql(today), { staleTime: TWELVE_HOURS_MS, retry: 1 });

  const periods: FiscalPeriodRow[] = useMemo(
    () =>
      toRecords(query.data)
        .filter(
          (r) =>
            (r?.Type === 'Month' || r?.Type === 'Quarter' || r?.Type === 'Year') && r?.StartDate && r?.EndDate,
        )
        .map((r) => ({ type: r.Type, start: String(r.StartDate), end: String(r.EndDate) })),
    [query.data],
  );

  const loading = query.isLoading;
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return undefined;
    }
    const t = setTimeout(() => setTimedOut(true), CALENDAR_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [loading]);

  // Wait only while the request is actually in flight. If it can't run (no toolbox token leaves the query
  // idle), fails, or hangs, fall back to All with a notice rather than holding the page's data hostage.
  const status = query.isSuccess ? 'ready' : loading && !timedOut ? 'loading' : 'error';
  const selection = useMemo(
    () => selectTimeFilter({ periodId, today, periods, status }),
    [periodId, today, periods, status],
  );

  return { periodId, setPeriodId, periods, ...selection };
}
